import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { CompanyService } from '../../../rent/service/data/company.service';
import { LocalDate } from '../../../core/model/local-date';
import { ReportService } from '../../service/data/report.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { Subscription } from 'rxjs';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-not-fully-paid-invoice',
    templateUrl: './not-fully-paid-invoice.component.html',
    styleUrls: ['./not-fully-paid-invoice.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NotFullyPaidInvoiceComponent implements OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    companyService: CompanyService,
    private _articleService: ArticleService,
    private _nativeWindow: NativeWindowService,
    private _dataService: ReportService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    const today = new Date();
    this.companyService = companyService;
    const reconciliationMonth = LocalDate.fromDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
    this.reconciliationTo = LocalDate.fromDate(
      new Date(reconciliationMonth.toDate().getFullYear(), reconciliationMonth.toDate().getMonth() + 1, 0)
    );

    this.form = fb.group({
      company: fcf.createMandatory('COMMON.COMPANY', null),
      articleId: fcf.create('ARTICLE.EDIT_HEADER', null),
      reconciliationTo: fb.group({}),
      groupLevel: fcf.createMandatory('REPORT.GROUP_LEVEL', 'company'),
      personalData: fcf.create('COMMON.DISPLAY_PERSONAL_DATA', 'no'),
    });

    this.columnDefinitions = [
      this._cdf.create('INVOICE.NUMBER_OF_INVOICES', 'numberOfInvoices'),
      this._cdf.create('INVOICE.AVERAGE_DAYS_LATE', 'averageDaysLate'),
      this._cdf.createMoney('INVOICE.REMAINING_AMOUNT', 'unPaidAmount'),
    ];
    this.futureInvoicesColumnDefinitions = [
      this._cdf.create('INVOICE.NUMBER_OF_INVOICES', 'numberOfFutureInvoices'),
      this._cdf.create('INVOICE.AVERAGE_DAYS_LATE', 'averageDaysLateFutureInvoices'),
      this._cdf.createMoney('INVOICE.BALANCE_CLAIM_ACCOUNT', 'unPaidAmountFutureInvoices'),
    ];
  }

  companyService: CompanyService;
  columnDefinitions: ColumnDefinition[] = [];
  futureInvoicesColumnDefinitions: ColumnDefinition[];
  
  articles: { name: string; value: string }[];
  groupSelection: { name: string; value: string }[];
  personalDataSelection: { name: string; value: string }[];
  dataContext: ExpandedListViewItemDefinition[] = [];
  futureInvoices: ExpandedListViewItemDefinition[];
  form: FormGroup;
  protected expandAllRows: boolean = false;

  reconciliationTo: LocalDate;

  ngOnInit() {
   

    this._articleService.getList().subscribe(data => {
      this.articles = [{ name: 'ALL_TEXT', value: null }];
      data.forEach(item => {
        this.articles.push({ name: item.toString(), value: item.id });
      });
    });

    this.groupSelection = [{ name: 'COMMON.COMPANY', value: 'company' }];
    this.groupSelection.push({ name: 'INVOICE.DEBTOR', value: 'contact' });

    this.personalDataSelection = [{ name: 'NO_TEXT', value: 'no' }];
    this.personalDataSelection.push({ name: 'YES_TEXT', value: 'yes' });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  private _subscription: Subscription;

  exportExcel(): void {
    if (!canSubmitReportForm(this.form)) return;

    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    this._subscription = this._dataService.getNotFullyPaidInvoicesExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;
    this.expandAllRows = false;

    this._dataService.getNotFullyPaidInvoicesReport(this.form.value).subscribe(data => {
      this.dataContext = data
        ? data.map(overview => {
            const model = new ExpandedListViewItemDefinition();
            model.caption = overview.level1Name;
            model.columnDefinitions = [
              this._cdf.create('INVOICE.INVOICE_NUMBER', 'level2Name'),
              this._cdf.create('INVOICE.DEBTOR', 'debtorOneContact'),
              this._cdf.create('INVOICE.DAYS_LATE', 'daysLate'),
              this._cdf.createDate('INVOICE.DUE_DATE', 'dueDate'),
              this._cdf.createDate('INVOICE.DEFERMENT_DATE', 'defermentDate', ColumnVisibility.optional),
              this._cdf.createDate('INVOICE.SENT_TO_DEBT_COLLECTION', 'sentToDebtCollection', ColumnVisibility.optional),
              this._cdf.create('INVOICE.PERIOD', 'refersToPeriod'),
              this._cdf.createMoney('INVOICE.REMAINING_AMOUNT', 'unPaidAmount'),
            ];
            model.dataContext = overview;
            model.itemsSource = overview.notFullyPaidInvoicesLevel2Models.map(x => {
              return {
                level2Name: x.level2Name,
                debtorOneContact: x.debtorOneContact,
                daysLate: x.daysLate,
                dueDate: LocalDate.fromDate(x.dueDate),
                defermentDate: LocalDate.fromDate(x.defermentDate),
                sentToDebtCollection: LocalDate.fromDate(x.sentToDebtCollection),
                refersToPeriod: x.refersToPeriod,
                unPaidAmount: x.unPaidAmount,
              };
            });

            return model;
          })
        : [];

      this.futureInvoices = data
        .map(overview => {
          if (overview.preBookedFutureInvoices.length == 0) return null;
          const model = new ExpandedListViewItemDefinition();
          model.caption = overview.level1NameFutureInvoices;
          model.columnDefinitions = [
            this._cdf.create('INVOICE.INVOICE_NUMBER', 'level2Name'),
            this._cdf.create('INVOICE.DEBTOR', 'debtorOneContact'),
            this._cdf.create('INVOICE.DAYS_LATE', 'daysLate'),
            this._cdf.createDate('INVOICE.DUE_DATE', 'dueDate'),
            this._cdf.createDate('INVOICE.DEFERMENT_DATE', 'defermentDate', ColumnVisibility.optional),
            this._cdf.createDate('INVOICE.SENT_TO_DEBT_COLLECTION', 'sentToDebtCollection', ColumnVisibility.optional),
            this._cdf.create('INVOICE.PERIOD', 'refersToPeriod'),
            this._cdf.createMoney('INVOICE.BALANCE_CLAIM_ACCOUNT', 'unPaidAmount'),
          ];
          model.dataContext = overview;
          model.itemsSource = overview.preBookedFutureInvoices.map(x => {
            return {
              level2Name: x.level2Name,
              debtorOneContact: x.debtorOneContact,
              daysLate: x.daysLate,
              dueDate: LocalDate.fromDate(x.dueDate),
              defermentDate: LocalDate.fromDate(x.defermentDate),
              sentToDebtCollection: LocalDate.fromDate(x.sentToDebtCollection),
              refersToPeriod: x.refersToPeriod,
              unPaidAmount: x.unPaidAmount,
            };
          });

          return model;
        })
        .filter(m => m != null);
    });
  }

  toggleExpandAllRows() {
    this.expandAllRows = !this.expandAllRows;
  }
}
